import { useCallback, useLayoutEffect, useRef } from 'react';

const DRAWER_AVAILABLE_HEIGHT_VARIABLE = '--results-filters-drawer-available-height';
const RESULTS_SCROLL_ROOT_ID = 'results-page';
const RESULTS_PRELUDE_SELECTOR = '.results-page__prelude';

export default function useDrawerAvailableHeight(drawerRef, enabled) {
  const animationFrameRef = useRef();

  const measureAvailableHeight = useCallback(() => {
    const drawerElement = drawerRef.current;
    const scrollRoot = document.getElementById(RESULTS_SCROLL_ROOT_ID);

    if (!drawerElement || !scrollRoot) return;

    const drawerRow = drawerElement.closest('.flex-item-height-fix') || drawerElement;
    const scrollRootRect = scrollRoot.getBoundingClientRect();
    const drawerRowRect = drawerRow.getBoundingClientRect();
    const viewportHeight = scrollRoot.clientHeight || scrollRootRect.height || window.innerHeight || 0;
    const drawerTopOffset = Math.min(viewportHeight, Math.max(0, drawerRowRect.top - scrollRootRect.top));
    const availableHeight = Math.max(0, Math.ceil(viewportHeight - drawerTopOffset));
    const nextHeight = `${availableHeight}px`;

    if (drawerElement.style.getPropertyValue(DRAWER_AVAILABLE_HEIGHT_VARIABLE) !== nextHeight) {
      drawerElement.style.setProperty(DRAWER_AVAILABLE_HEIGHT_VARIABLE, nextHeight);
    }
  }, [drawerRef]);

  const scheduleMeasurement = useCallback(() => {
    if (animationFrameRef.current !== undefined) return;

    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    animationFrameRef.current = requestFrame(() => {
      animationFrameRef.current = undefined;
      measureAvailableHeight();
    });
  }, [measureAvailableHeight]);

  useLayoutEffect(() => {
    const drawerElement = drawerRef.current;
    const scrollRoot = document.getElementById(RESULTS_SCROLL_ROOT_ID);

    if (!enabled) {
      drawerElement?.style.removeProperty(DRAWER_AVAILABLE_HEIGHT_VARIABLE);
      return undefined;
    }

    if (!drawerElement || !scrollRoot) return undefined;

    const pageHeader = scrollRoot.querySelector('header');
    const prelude = scrollRoot.querySelector(RESULTS_PRELUDE_SELECTOR);
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleMeasurement) : undefined;

    // Measure synchronously before paint, then coalesce subsequent scroll/resize events to one
    // geometry read and, only when the rounded value changed, one style write per rendered frame.
    measureAvailableHeight();
    scrollRoot.addEventListener('scroll', scheduleMeasurement, { passive: true });
    window.addEventListener('resize', scheduleMeasurement);
    resizeObserver?.observe(scrollRoot);
    if (pageHeader) resizeObserver?.observe(pageHeader);
    if (prelude) resizeObserver?.observe(prelude);

    return () => {
      scrollRoot.removeEventListener('scroll', scheduleMeasurement);
      window.removeEventListener('resize', scheduleMeasurement);
      resizeObserver?.disconnect();

      if (animationFrameRef.current !== undefined) {
        cancelFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      drawerElement.style.removeProperty(DRAWER_AVAILABLE_HEIGHT_VARIABLE);
    };
  }, [drawerRef, enabled, measureAvailableHeight, scheduleMeasurement]);
}
